import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Register custom fonts (same as production TemplatesPage)
const FontStyle = Quill.import('attributors/style/font');
FontStyle.whitelist = [
  'Monotype Corsiva, Dancing Script, cursive',
  'Dancing Script, cursive',
  'Great Vibes, cursive',
  'Parisienne, cursive',
  'Sacramento, cursive',
  'Playfair Display, serif',
  'Georgia, Times New Roman, serif',
  'Times New Roman, Times, serif',
  'Arial, Helvetica, sans-serif',
  'Courier New, Courier, monospace'
];
Quill.register(FontStyle, true);
const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px'];
Quill.register(SizeStyle, true);

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  FlaskConical, Plus, Trash2, Save, Loader2, Send, Eye, Edit2,
  Building2, FileText, Play, X, ChevronDown, ChevronRight, RefreshCw,
  Image, Upload, ImagePlus, Check, Clock, Mail, Phone, Globe, Link2, Maximize2, Paintbrush
} from 'lucide-react';
import ManualTemplateBuilder from '../components/ManualTemplateBuilder';

const API = process.env.REACT_APP_BACKEND_URL;

const BRAND_OPTIONS = [
  'Alfa Romeo', 'Audi', 'BMW', 'Buick', 'Cadillac', 'Chevrolet', 'Chrysler', 'Dodge',
  'FIAT', 'Ford', 'Genesis', 'GMC', 'Honda', 'Hyundai', 'INFINITI', 'Jaguar', 'Jeep',
  'Kia', 'Land Rover', 'Lexus', 'Lincoln', 'Maserati', 'Mazda', 'Mercedes-Benz', 'MINI',
  'Mitsubishi', 'Nissan', 'RAM', 'Subaru', 'Toyota', 'Volkswagen', 'Volvo',
];

const LEAD_TYPES = [
  { value: 'new_vehicle_inquiry_vin', label: 'New Vehicle (VIN)', color: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' },
  { value: 'new_vehicle_inquiry', label: 'New Vehicle', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  { value: 'used_vehicle_inquiry', label: 'Used Vehicle', color: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
  { value: 'used_vehicle_inquiry_cpo', label: 'Used Vehicle (CPO)', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  { value: 'general_inquiry', label: 'General Inquiry', color: 'bg-slate-500/10 text-slate-400 border-slate-500/20' },
];

const TEMPLATE_VARS = [
  { key: '{{customer_name}}', label: 'Customer Name' },
  { key: '{{vehicle_year}}', label: 'Vehicle Year' },
  { key: '{{vehicle_make}}', label: 'Vehicle Make' },
  { key: '{{vehicle_model}}', label: 'Vehicle Model' },
  { key: '{{dealership_name}}', label: 'Dealership Name' },
  { key: '{{dealership_phone}}', label: 'Dealership Phone' },
  { key: '{{dealership_address}}', label: 'Dealership Address' },
  { key: '{{dealership_website}}', label: 'Dealership Website' },
  { key: '{{hours_directions}}', label: 'Hours & Directions' },
  { key: '{{vin}}', label: 'VIN' },
  { key: '{{vehicle_direct}}', label: 'Vehicle Photos (VDP)' },
  { key: '{{vehicle_link}}', label: 'Vehicle Link' },
  { key: '{{deal_testdrive_buttons}}', label: 'Deal + Test Drive Buttons' },
  { key: '{{vehicle_testdrive}}', label: 'Test Drive Button' },
  { key: '{{preowned_specials}}', label: 'Pre-Owned Specials Button' },
  { key: '{{testdrive_preowned_buttons}}', label: 'Test Drive + Pre-Owned Specials' },
  { key: '{{recommended_vehicles}}', label: 'Similar Vehicles (3 cards)' },
  { key: '{{similar_new_vehicles}}', label: 'Similar New Vehicles (3 cards)' },
  { key: '{{similar_preowned_vehicles}}', label: 'Similar Pre-Owned Vehicles (3 cards)' },
  { key: '{{new_vehicle_specials}}', label: 'New Vehicle Specials (3 cards)' },
  { key: '{{search_new_vehicles}}', label: 'Search New Vehicles Button' },
  { key: '{{search_preowned_vehicles}}', label: 'Search Pre-Owned Vehicles Button' },
  { key: '{{carfax_link}}', label: 'CarFax Report' },
  { key: '{{ai_response}}', label: 'AI Response' },
  { key: '{{email_buttons}}', label: 'Email Buttons' },
  { key: '{{vehicle_image}}', label: 'Vehicle Image (Repo)' },
];

const quillFormats = [
  'header', 'font', 'size', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'align', 'list', 'indent', 'blockquote', 'link', 'image'
];

const formatPhone = (value) => {
  if (!value) return '';
  const d = value.replace(/\D/g, '');
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0,3)}-${d.slice(3)}`;
  return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6,10)}`;
};

export default function SamplesPage() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [dealerships, setDealerships] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedDlr, setSelectedDlr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [simResult, setSimResult] = useState(null);
  const [expandedSection, setExpandedSection] = useState('dealership');

  // Dialogs
  const [showDlrDialog, setShowDlrDialog] = useState(false);
  const [editingDlr, setEditingDlr] = useState(null);
  const [showTplEditor, setShowTplEditor] = useState(false);
  const [editingTpl, setEditingTpl] = useState(null);
  const [showLogoLibrary, setShowLogoLibrary] = useState(false);
  const [logoLibrary, setLogoLibrary] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showTplPreview, setShowTplPreview] = useState(false);
  const [showHistoryDetail, setShowHistoryDetail] = useState(null);
  const [showResponsePreview, setShowResponsePreview] = useState(false);
  const [rightPanel, setRightPanel] = useState('simulation'); // 'simulation' | 'manual'
  const quillRef = useRef(null);

  // Dealership form
  const [dlrForm, setDlrForm] = useState({ name: '', phone: '', address: '', website: '', brands: [], brand_websites: {}, hours_url: '', test_drive_url: '', preowned_vehicles_url: '', preowned_specials_url: '', window_sticker_url_template: '' });

  // Template editor form
  const [tplForm, setTplForm] = useState({
    name: '', subject: '', body: '', logo_url: '',
    show_window_sticker: false, show_warranty_btn: false, show_similar_vehicles_btn: false, show_test_drive_btn: false, show_new_inventory_btn: false,
    show_preowned_btn: false, show_cpo_benefits: false, show_cpo_vs_used: false, show_cpo_inspection: false,
  });

  // Simulation form
  const [simForm, setSimForm] = useState({
    lead_type: 'new_vehicle_inquiry_vin', customer_name: '', customer_email: '', customer_phone: '',
    vehicle_year: '', vehicle_make: '', vehicle_model: '', vin: '',
    send_email: false, send_sms: false, recipient_email: '', recipient_phone: '',
  });

  // Quill modules
  const linkHandler = useCallback(function(value) {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    if (value) {
      const range = quill.getSelection();
      if (range) { const url = prompt('Enter URL:'); if (url) quill.format('link', url); }
    } else {
      const range = quill.getSelection();
      if (range && range.length > 0) { quill.format('link', false); quill.formatText(range.index, range.length, { bold: false, underline: false, color: false }); }
    }
  }, []);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        [{ font: [false, 'Monotype Corsiva, Dancing Script, cursive', 'Dancing Script, cursive', 'Great Vibes, cursive', 'Parisienne, cursive', 'Sacramento, cursive', 'Playfair Display, serif', 'Georgia, Times New Roman, serif', 'Times New Roman, Times, serif', 'Arial, Helvetica, sans-serif', 'Courier New, Courier, monospace'] }],
        [{ size: ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: { link: linkHandler }
    }
  }), [linkHandler]);

  // ── Data Fetching ─────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      const dlrRes = await axios.get(`${API}/api/samples/dealerships`, { headers });
      const dlrs = dlrRes.data || [];
      setDealerships(dlrs);
      if (dlrs.length > 0) {
        const first = selectedDlr ? dlrs.find(d => d.dealership_id === selectedDlr.dealership_id) || dlrs[0] : dlrs[0];
        setSelectedDlr(first);
        const [tplRes, histRes] = await Promise.all([
          axios.get(`${API}/api/samples/templates?dealership_id=${first.dealership_id}`, { headers }),
          axios.get(`${API}/api/samples/history?dealership_id=${first.dealership_id}`, { headers }),
        ]);
        setTemplates(tplRes.data || []);
        setHistory(histRes.data || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const selectDealership = async (dlr) => {
    setSelectedDlr(dlr);
    try {
      const [tplRes, histRes] = await Promise.all([
        axios.get(`${API}/api/samples/templates?dealership_id=${dlr.dealership_id}`, { headers }),
        axios.get(`${API}/api/samples/history?dealership_id=${dlr.dealership_id}`, { headers }),
      ]);
      setTemplates(tplRes.data || []);
      setHistory(histRes.data || []);
    } catch (e) { console.error(e); }
  };

  // ── Dealership CRUD ───────────────────────────────
  const openNewDlr = () => {
    setEditingDlr(null);
    setDlrForm({ name: '', phone: '', address: '', website: '', brands: [], brand_websites: {}, hours_url: '', test_drive_url: '', preowned_vehicles_url: '', preowned_specials_url: '', window_sticker_url_template: '' });
    setShowDlrDialog(true);
  };

  const openEditDlr = (dlr) => {
    setEditingDlr(dlr);
    setDlrForm({
      name: dlr.name || '', phone: dlr.phone || '', address: dlr.address || '',
      website: dlr.website || '', brands: dlr.brands || [], brand_websites: dlr.brand_websites || {},
      hours_url: dlr.hours_url || '', test_drive_url: dlr.test_drive_url || '',
      preowned_vehicles_url: dlr.preowned_vehicles_url || '',
      preowned_specials_url: dlr.preowned_specials_url || '',
      window_sticker_url_template: dlr.window_sticker_url_template || '',
    });
    setShowDlrDialog(true);
  };

  const saveDealership = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingDlr) {
        const res = await axios.put(`${API}/api/samples/dealerships/${editingDlr.dealership_id}`, dlrForm, { headers });
        toast.success('Dealership updated');
        setSelectedDlr(res.data);
      } else {
        const res = await axios.post(`${API}/api/samples/dealerships`, dlrForm, { headers });
        toast.success('Dealership created with 5 sample templates');
        setSelectedDlr(res.data);
      }
      setShowDlrDialog(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const deleteDealership = async (dlrId) => {
    if (!window.confirm('Delete this sample dealership and all its templates?')) return;
    try {
      await axios.delete(`${API}/api/samples/dealerships/${dlrId}`, { headers });
      toast.success('Dealership deleted');
      if (selectedDlr?.dealership_id === dlrId) { setSelectedDlr(null); setTemplates([]); setHistory([]); }
      fetchData();
    } catch (e) { toast.error('Delete failed'); }
  };

  // ── Template CRUD ─────────────────────────────────
  const openEditTpl = (tpl) => {
    setEditingTpl(tpl);
    setTplForm({
      name: tpl.name || '', subject: tpl.subject || '', body: tpl.body || '', logo_url: tpl.logo_url || '',
      show_window_sticker: tpl.show_window_sticker || false, show_warranty_btn: tpl.show_warranty_btn || false,
      show_similar_vehicles_btn: tpl.show_similar_vehicles_btn || false, show_test_drive_btn: tpl.show_test_drive_btn || false,
      show_preowned_btn: tpl.show_preowned_btn || false,
      show_cpo_benefits: tpl.show_cpo_benefits || false, show_cpo_vs_used: tpl.show_cpo_vs_used || false,
      show_cpo_inspection: tpl.show_cpo_inspection || false,
    });
    setShowTplPreview(false);
    setShowLogoLibrary(false);
    setShowTplEditor(true);
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/api/samples/templates/${editingTpl.template_id}`, tplForm, { headers });
      toast.success('Template updated');
      setShowTplEditor(false);
      const tplRes = await axios.get(`${API}/api/samples/templates?dealership_id=${selectedDlr.dealership_id}`, { headers });
      setTemplates(tplRes.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const resetTemplates = async () => {
    if (!selectedDlr || !window.confirm('Reset all templates to defaults?')) return;
    try {
      await axios.post(`${API}/api/samples/templates/reset/${selectedDlr.dealership_id}`, {}, { headers });
      toast.success('Templates reset to defaults');
      const tplRes = await axios.get(`${API}/api/samples/templates?dealership_id=${selectedDlr.dealership_id}`, { headers });
      setTemplates(tplRes.data || []);
    } catch (e) { toast.error('Reset failed'); }
  };

  const deleteSimulation = async (simId) => {
    try {
      await axios.delete(`${API}/api/samples/history/${simId}`, { headers });
      setHistory(prev => prev.filter(s => s.simulation_id !== simId));
      toast.success('Simulation deleted');
    } catch (e) { toast.error('Delete failed'); }
  };

  const fetchLogoLib = async () => {
    try { const res = await axios.get(`${API}/api/upload/logos`, { headers }); setLogoLibrary(res.data); } catch (e) { /* ignore */ }
  };

  const handleLogoUpload = async (file) => {
    if (!file || file.size > 2 * 1024 * 1024) { toast.error('Max 2MB'); return; }
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/api/upload/logo`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setTplForm(p => ({ ...p, logo_url: res.data.logo_url }));
      toast.success('Logo uploaded');
      fetchLogoLib();
    } catch (e) { toast.error('Upload failed'); }
    setUploading(false);
  };

  const insertVariable = (varKey) => {
    const quill = quillRef.current?.getEditor?.();
    if (quill) {
      const range = quill.getSelection() || { index: quill.getLength() - 1 };
      quill.insertText(range.index, varKey);
      quill.setSelection(range.index + varKey.length);
    } else {
      setTplForm(p => ({ ...p, body: p.body + varKey }));
    }
  };

  // ── Simulation ────────────────────────────────────
  const runSimulation = async () => {
    if (!selectedDlr) return;
    setSimulating(true); setSimResult(null);
    try {
      const payload = { ...simForm, dealership_id: selectedDlr.dealership_id };
      const res = await axios.post(`${API}/api/samples/simulate`, payload, { headers, timeout: 55000 });
      setSimResult(res.data);
      if (res.data.success) {
        toast.success('Simulation complete!');
        if (res.data.email_sent) toast.success('Email sent');
        if (res.data.email_error && simForm.send_email) toast.error(`Email: ${res.data.email_error}`);
        if (res.data.sms_sent) toast.success('SMS sent');
        if (res.data.sms_error && simForm.send_sms) toast.error(`SMS: ${res.data.sms_error}`);
      }
      // Refresh history
      const histRes = await axios.get(`${API}/api/samples/history?dealership_id=${selectedDlr.dealership_id}`, { headers });
      setHistory(histRes.data || []);
    } catch (e) {
      const msg = e.code === 'ECONNABORTED' ? 'Simulation timed out — try a simpler lead type or remove VIN'
        : e.response?.data?.detail || 'Simulation failed';
      toast.error(msg);
    }
    setSimulating(false);
  };

  const selectedType = LEAD_TYPES.find(t => t.value === simForm.lead_type);
  const needsVehicle = simForm.lead_type !== 'general_inquiry';

  if (loading) return (
    <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-red-600" /></div>
  );

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="samples-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase tracking-tight">Samples</h1>
          <p className="text-slate-400 text-sm mt-2">Test and simulate response templates — isolated from production</p>
        </div>
        <Badge variant="outline" className="border-amber-600/30 text-amber-400 text-xs">
          Sends from samples@etarsp.com
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ── Left Column ── */}
        <div className="lg:col-span-4 space-y-4">
          {/* Dealership Section — Collapsible */}
          <Card className="bg-[#0A0A0A] border-[#262626]">
            <div
              role="button" tabIndex={0}
              onClick={() => setExpandedSection(expandedSection === 'dealership' ? '' : 'dealership')}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedSection(expandedSection === 'dealership' ? '' : 'dealership'); } }}
              className="w-full flex items-center justify-between p-4 hover:bg-[#111] transition-colors rounded-t-lg cursor-pointer"
              data-testid="toggle-dealership-section"
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-red-500" strokeWidth={1.5} />
                <span className="text-sm font-bold tracking-widest text-white uppercase">Sample Dealership</span>
                <Badge variant="secondary" className="bg-[#262626] text-slate-400 text-xs ml-1">{dealerships.length}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="new-sample-dlr-btn"
                  onClick={(e) => { e.stopPropagation(); openNewDlr(); }}
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white text-xs h-7"
                >
                  <Plus className="w-3 h-3 mr-1" /> New
                </Button>
                {expandedSection === 'dealership' ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
              </div>
            </div>
            {expandedSection === 'dealership' && (
              <div className="px-4 pb-4 space-y-1">
                {dealerships.length === 0 ? (
                  <div className="text-center py-8">
                    <FlaskConical className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No sample dealerships yet</p>
                    <p className="text-xs text-slate-600 mt-1">Create one to start testing</p>
                  </div>
                ) : dealerships.map(dlr => (
                  <div
                    key={dlr.dealership_id}
                    data-testid={`sample-dlr-${dlr.dealership_id}`}
                    className={`flex items-center justify-between p-2.5 rounded-md cursor-pointer transition-all
                      ${selectedDlr?.dealership_id === dlr.dealership_id
                        ? 'bg-red-600/10 border border-red-600/30'
                        : 'hover:bg-[#171717] border border-transparent'}`}
                    onClick={() => selectDealership(dlr)}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{dlr.name}</p>
                      <p className="text-xs text-slate-500 truncate">{(dlr.brands || []).join(', ') || 'No brands'}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); openEditDlr(dlr); }} className="p-1 text-slate-500 hover:text-white" data-testid={`edit-dlr-${dlr.dealership_id}`}>
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); deleteDealership(dlr.dealership_id); }} className="p-1 text-slate-500 hover:text-red-400" data-testid={`delete-dlr-${dlr.dealership_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Templates Section — Collapsible */}
          {selectedDlr && (
            <Card className="bg-[#0A0A0A] border-[#262626]">
              <div
                role="button" tabIndex={0}
                onClick={() => setExpandedSection(expandedSection === 'templates' ? '' : 'templates')}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedSection(expandedSection === 'templates' ? '' : 'templates'); } }}
                className="w-full flex items-center justify-between p-4 hover:bg-[#111] transition-colors rounded-t-lg cursor-pointer"
                data-testid="toggle-templates-section"
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-red-500" strokeWidth={1.5} />
                  <span className="text-sm font-bold tracking-widest text-white uppercase">Templates</span>
                  <Badge variant="secondary" className="bg-[#262626] text-slate-400 text-xs ml-1">{templates.length}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={(e) => { e.stopPropagation(); resetTemplates(); }} size="sm" variant="outline" className="border-[#262626] text-slate-400 text-xs h-7" data-testid="reset-templates-btn">
                    <RefreshCw className="w-3 h-3 mr-1" /> Reset
                  </Button>
                  {expandedSection === 'templates' ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                </div>
              </div>
              {expandedSection === 'templates' && (
                <div className="px-4 pb-4 space-y-2">
                  {templates.map(tpl => {
                    const typeInfo = LEAD_TYPES.find(t => t.value === tpl.lead_type) || {};
                    return (
                      <Card key={tpl.template_id} className="bg-[#111] border-[#262626] hover:border-[#404040] transition-all" data-testid={`template-card-${tpl.lead_type}`}>
                        <div className="p-3">
                          {tpl.logo_url && (
                            <div className="text-center mb-2 pb-2 border-b border-[#1A1A1A]">
                              <img src={tpl.logo_url} alt="" className="max-h-8 inline-block object-contain" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
                            </div>
                          )}
                          <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                              <h4 className="text-sm font-semibold text-white truncate">{tpl.name}</h4>
                              <Badge className={`mt-1 text-[9px] font-bold tracking-wider uppercase border ${typeInfo.color || ''}`}>
                                {typeInfo.label || tpl.lead_type}
                              </Badge>
                            </div>
                            <Button variant="outline" size="sm" onClick={() => openEditTpl(tpl)} className="border-[#333] bg-transparent hover:bg-[#1a1a1a] text-white text-[10px] font-bold tracking-wider uppercase h-8" data-testid={`edit-tpl-${tpl.template_id}`}>
                              <Edit2 className="w-3 h-3 mr-1" /> Edit
                            </Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </Card>
          )}

          {/* Response Log — Collapsible */}
          {selectedDlr && (
            <Card className="bg-[#0A0A0A] border-[#262626]">
              <div
                role="button" tabIndex={0}
                onClick={() => setExpandedSection(expandedSection === 'history' ? '' : 'history')}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedSection(expandedSection === 'history' ? '' : 'history'); } }}
                className="w-full flex items-center justify-between p-4 hover:bg-[#111] transition-colors rounded-t-lg cursor-pointer"
                data-testid="toggle-history-section"
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-red-500" strokeWidth={1.5} />
                  <span className="text-sm font-bold tracking-widest text-white uppercase">Response Log</span>
                  <Badge variant="secondary" className="bg-[#262626] text-slate-400 text-xs ml-1">{history.length}</Badge>
                </div>
                {expandedSection === 'history' ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
              </div>
              {expandedSection === 'history' && (
                <div className="px-4 pb-4 space-y-1 max-h-80 overflow-y-auto">
                  {history.length === 0 ? (
                    <p className="text-xs text-slate-500 text-center py-4">No simulations yet</p>
                  ) : history.map(sim => {
                    const typeInfo = LEAD_TYPES.find(t => t.value === sim.lead_type) || {};
                    return (
                      <div
                        key={sim.simulation_id}
                        className="p-2.5 rounded-md border border-[#262626] hover:border-[#404040] cursor-pointer transition-all"
                        onClick={() => setShowHistoryDetail(sim)}
                        data-testid={`history-${sim.simulation_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <Badge className={`text-[9px] font-bold tracking-wider uppercase border ${typeInfo.color || ''}`}>
                            {typeInfo.label || sim.lead_type}
                          </Badge>
                          <div className="flex gap-1 items-center">
                            {sim.email_sent && <Badge className="bg-green-600/20 text-green-400 text-[8px]">Email</Badge>}
                            {sim.sms_sent && <Badge className="bg-green-600/20 text-green-400 text-[8px]">SMS</Badge>}
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteSimulation(sim.simulation_id); }}
                              className="p-1 text-slate-600 hover:text-red-400 transition-colors"
                              data-testid={`delete-sim-${sim.simulation_id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-white mt-1 truncate">{sim.customer_name || 'Unknown'} — {sim.vehicle_year} {sim.vehicle_make} {sim.vehicle_model}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">{new Date(sim.created_at).toLocaleString()}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          )}

          {/* Manual Template Builder — Toggle for right panel */}
          <Card
            className={`bg-[#0A0A0A] border-[#262626] cursor-pointer transition-all ${rightPanel === 'manual' ? 'border-red-600/50 bg-red-600/5' : 'hover:border-[#404040]'}`}
            onClick={() => setRightPanel(rightPanel === 'manual' ? 'simulation' : 'manual')}
            data-testid="toggle-manual-builder-section"
          >
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-2">
                <Paintbrush className="w-5 h-5 text-red-500" strokeWidth={1.5} />
                <span className="text-sm font-bold tracking-widest text-white uppercase">Manual Builder</span>
              </div>
              {rightPanel === 'manual' ? (
                <span className="text-[10px] font-bold tracking-wider text-red-400 uppercase">Active</span>
              ) : (
                <ChevronRight className="w-4 h-4 text-slate-500" />
              )}
            </div>
          </Card>
        </div>

        {/* ── Right Column — Simulation Engine or Manual Builder ── */}
        <div className="lg:col-span-8">
          {rightPanel === 'manual' ? (
            <Card className="bg-[#0A0A0A] border-[#262626] p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Paintbrush className="w-5 h-5 text-red-500" strokeWidth={1.5} />
                  <span className="text-sm font-bold tracking-widest text-white uppercase">Manual Template Builder</span>
                </div>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setRightPanel('simulation')}
                  className="border-[#333] text-[10px] font-bold tracking-wider uppercase text-slate-400 h-7"
                  data-testid="back-to-simulation"
                >
                  <Play className="w-3 h-3 mr-1" /> Back to Simulation
                </Button>
              </div>
              <ManualTemplateBuilder />
            </Card>
          ) : (
          <Card className="bg-[#0A0A0A] border-[#262626] p-4">
            <div className="flex items-center gap-2 mb-4">
              <Play className="w-5 h-5 text-red-500" strokeWidth={1.5} />
              <span className="text-sm font-bold tracking-widest text-white uppercase">Simulation Engine</span>
            </div>

            {selectedDlr ? (
              <div className="space-y-4" data-testid="simulation-panel">
                {/* Lead Type Selector */}
                <div>
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-2 block">Simulation Type</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                    {LEAD_TYPES.map(type => (
                      <button key={type.value} data-testid={`sim-type-${type.value}`}
                        onClick={() => setSimForm(p => ({ ...p, lead_type: type.value }))}
                        className={`p-2.5 rounded-lg border text-xs font-medium transition-all
                          ${simForm.lead_type === type.value ? 'border-red-600 bg-red-600/10 text-red-400' : 'border-[#262626] bg-[#111] text-slate-400 hover:border-[#404040] hover:text-white'}`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Customer Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Customer Name</Label>
                    <Input data-testid="sim-customer-name" value={simForm.customer_name} onChange={e => setSimForm(p => ({ ...p, customer_name: e.target.value }))} placeholder="John Smith" className="mt-1 bg-[#111] border-[#333] text-white h-10" />
                  </div>
                  <div>
                    <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Customer Email</Label>
                    <Input data-testid="sim-customer-email" value={simForm.customer_email} onChange={e => setSimForm(p => ({ ...p, customer_email: e.target.value }))} placeholder="john@example.com" className="mt-1 bg-[#111] border-[#333] text-white h-10" />
                  </div>
                  <div>
                    <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Customer Phone</Label>
                    <Input data-testid="sim-customer-phone" value={simForm.customer_phone} onChange={e => setSimForm(p => ({ ...p, customer_phone: e.target.value }))} placeholder="(555) 555-0100" className="mt-1 bg-[#111] border-[#333] text-white h-10" />
                  </div>
                </div>

                {/* Vehicle Info */}
                {needsVehicle && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Year</Label>
                      <Input data-testid="sim-vehicle-year" value={simForm.vehicle_year} onChange={e => setSimForm(p => ({ ...p, vehicle_year: e.target.value }))} placeholder="2025" className="mt-1 bg-[#111] border-[#333] text-white h-10" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Make</Label>
                      <Input data-testid="sim-vehicle-make" value={simForm.vehicle_make} onChange={e => setSimForm(p => ({ ...p, vehicle_make: e.target.value }))} placeholder="Cadillac" className="mt-1 bg-[#111] border-[#333] text-white h-10" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Model</Label>
                      <Input data-testid="sim-vehicle-model" value={simForm.vehicle_model} onChange={e => setSimForm(p => ({ ...p, vehicle_model: e.target.value }))} placeholder="Escalade" className="mt-1 bg-[#111] border-[#333] text-white h-10" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">VIN</Label>
                      <Input data-testid="sim-vin" value={simForm.vin} onChange={e => setSimForm(p => ({ ...p, vin: e.target.value.toUpperCase() }))} placeholder="1GYS4CKL5RR..." className="mt-1 bg-[#111] border-[#333] text-white h-10 font-mono" />
                    </div>
                  </div>
                )}

                {/* Send Options */}
                <div className="flex flex-wrap gap-4 border border-[#262626] rounded-lg p-3 bg-[#111]">
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" data-testid="sim-send-email" checked={simForm.send_email} onChange={e => setSimForm(p => ({ ...p, send_email: e.target.checked }))} className="accent-red-600" />
                      <span className="text-sm text-slate-300">Send Email</span>
                    </label>
                    {simForm.send_email && <Input data-testid="sim-recipient-email" value={simForm.recipient_email} onChange={e => setSimForm(p => ({ ...p, recipient_email: e.target.value }))} placeholder="recipient@email.com" className="w-56 bg-transparent border-[#333] text-white text-sm h-8" />}
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" data-testid="sim-send-sms" checked={simForm.send_sms} onChange={e => setSimForm(p => ({ ...p, send_sms: e.target.checked }))} className="accent-red-600" />
                      <span className="text-sm text-slate-300">Send SMS</span>
                    </label>
                    {simForm.send_sms && <Input data-testid="sim-recipient-phone" value={simForm.recipient_phone} onChange={e => setSimForm(p => ({ ...p, recipient_phone: e.target.value }))} placeholder="+1 (555) 555-0100" className="w-48 bg-transparent border-[#333] text-white text-sm h-8" />}
                  </div>
                </div>

                <Button data-testid="run-simulation-btn" onClick={runSimulation} disabled={simulating || !selectedDlr} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase h-11">
                  {simulating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Running Simulation...</> : <><Play className="w-4 h-4 mr-2" /> Run Simulation</>}
                </Button>

                {/* Result */}
                {simResult && (
                  <div className="border border-[#262626] rounded-lg bg-[#111] overflow-hidden" data-testid="simulation-result">
                    <div className="flex items-center justify-between p-3 border-b border-[#262626]">
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-red-500" />
                        <span className="text-xs font-bold text-white uppercase tracking-wider">Result</span>
                        <Badge className={`text-[9px] font-bold tracking-wider uppercase border ${selectedType?.color || ''}`}>{simResult.lead_type_label}</Badge>
                      </div>
                      <div className="flex gap-1 items-center">
                        {simResult.email_sent && <Badge className="bg-green-600/20 text-green-400 text-[10px]">Email Sent</Badge>}
                        {simResult.sms_sent && <Badge className="bg-green-600/20 text-green-400 text-[10px]">SMS Sent</Badge>}
                        {simResult.vehicle_found_on_website && <Badge className="bg-blue-600/20 text-blue-400 text-[10px]">VDP Found</Badge>}
                        <button
                          onClick={() => setShowResponsePreview(true)}
                          className="ml-1 p-1.5 rounded hover:bg-[#262626] text-slate-400 hover:text-white transition-colors"
                          title="Pop out preview"
                          data-testid="popout-preview-btn"
                        >
                          <Maximize2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="p-3">
                      <div className="mb-2">
                        <span className="text-[10px] text-slate-500 font-bold tracking-widest uppercase">Subject:</span>
                        <p className="text-sm text-white mt-0.5">{simResult.response_subject}</p>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-500 font-bold tracking-widest uppercase">Email Preview:</span>
                        <div className="mt-1 bg-white rounded-lg p-4 max-h-96 overflow-y-auto" style={{ color: '#1a1a1a' }}
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(simResult.response_body) }}
                          data-testid="email-preview" />
                      </div>
                      {simResult.sms_text && (
                        <div className="mt-3">
                          <span className="text-[10px] text-slate-500 font-bold tracking-widest uppercase">SMS Text:</span>
                          <p className="text-sm text-slate-300 mt-0.5 bg-[#0A0A0A] rounded p-2 font-mono">{simResult.sms_text}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-16">
                <Play className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Select or create a sample dealership to start simulating</p>
              </div>
            )}
          </Card>
          )}
        </div>
      </div>

      {/* ── Dealership Dialog ── */}
      <Dialog open={showDlrDialog} onOpenChange={setShowDlrDialog}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wider">
              {editingDlr ? 'Edit Sample Dealership' : 'Add Sample Dealership'}
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">Configure your sample dealership for testing templates.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveDealership} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Dealership Name</Label>
              <Input data-testid="sample-dlr-name" value={dlrForm.name} onChange={e => setDlrForm(p => ({ ...p, name: e.target.value }))} placeholder="Earnhardt Cadillac" className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Phone</Label>
                <Input data-testid="sample-dlr-phone" value={dlrForm.phone} onChange={e => setDlrForm(p => ({ ...p, phone: formatPhone(e.target.value) }))} placeholder="555-123-4567" className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600" />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Website</Label>
                <Input data-testid="sample-dlr-website" value={dlrForm.website} onChange={e => setDlrForm(p => ({ ...p, website: e.target.value }))} placeholder="https://www.example.com" className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Address</Label>
              <Input data-testid="sample-dlr-address" value={dlrForm.address} onChange={e => setDlrForm(p => ({ ...p, address: e.target.value }))} placeholder="123 Main St, City, ST" className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600" />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Dealership Hours URL</Label>
              <Input data-testid="sample-dlr-hours-url" value={dlrForm.hours_url} onChange={e => setDlrForm(p => ({ ...p, hours_url: e.target.value.trim() }))} placeholder="https://www.example.com/hours" className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600" />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Test Drive URL</Label>
              <Input data-testid="sample-dlr-test-drive-url" value={dlrForm.test_drive_url} onChange={e => setDlrForm(p => ({ ...p, test_drive_url: e.target.value.trim() }))} placeholder="https://www.example.com/testdrive.aspx" className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600" />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Pre-Owned Vehicles URL</Label>
              <Input data-testid="sample-dlr-preowned-url" value={dlrForm.preowned_vehicles_url} onChange={e => setDlrForm(p => ({ ...p, preowned_vehicles_url: e.target.value.trim() }))} placeholder="https://www.example.com/used-vehicles/" className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600" />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Pre-Owned Specials URL</Label>
              <Input data-testid="sample-dlr-preowned-specials-url" value={dlrForm.preowned_specials_url} onChange={e => setDlrForm(p => ({ ...p, preowned_specials_url: e.target.value.trim() }))} placeholder="https://www.example.com/specials/pre-owned/" className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600" />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Window Sticker URL Template</Label>
              <Input data-testid="sample-dlr-window-sticker-template" value={dlrForm.window_sticker_url_template} onChange={e => setDlrForm(p => ({ ...p, window_sticker_url_template: e.target.value.trim() }))} placeholder="https://example.com/api/.../windowsticker/kia?vin={vin}&accountId=xxx" className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600" />
              <p className="text-[11px] text-slate-600 mt-1">Use <code className="text-amber-500/70">{'{vin}'}</code> as placeholder.</p>
            </div>
            {/* Brands Dropdown */}
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Brands</Label>
              <Select value="" onValueChange={v => { if (v && !dlrForm.brands.includes(v)) setDlrForm(p => ({ ...p, brands: [...p.brands, v].sort() })); }}>
                <SelectTrigger className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm" data-testid="sample-brands-select">
                  <SelectValue placeholder="Select brands to add" />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#262626] max-h-60">
                  {BRAND_OPTIONS.filter(b => !dlrForm.brands.includes(b)).map(brand => (
                    <SelectItem key={brand} value={brand} className="text-white hover:bg-[#262626]">{brand}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dlrForm.brands.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="sample-brands-list">
                  {dlrForm.brands.map(brand => (
                    <span key={brand} className="inline-flex items-center gap-1 bg-red-500/10 text-red-400 border border-red-500/20 rounded-md px-2.5 py-1 text-xs font-medium">
                      {brand}
                      <button type="button" onClick={() => setDlrForm(p => ({ ...p, brands: p.brands.filter(b => b !== brand) }))} className="hover:text-white transition-colors" data-testid={`remove-brand-${brand}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            {/* Brand Websites */}
            {dlrForm.brands.length > 0 && (
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-2 block">Brand-Specific Websites</Label>
                <div className="space-y-2">
                  {dlrForm.brands.map(brand => (
                    <div key={brand} className="flex items-center gap-3">
                      <span className="text-xs text-slate-300 font-bold w-24 shrink-0">{brand}</span>
                      <Input value={(dlrForm.brand_websites || {})[brand] || ''} onChange={e => setDlrForm(p => ({ ...p, brand_websites: { ...p.brand_websites, [brand]: e.target.value } }))} placeholder={dlrForm.website || 'brand-specific-site.com'} className="bg-[#111] border-[#333] h-9 text-sm placeholder:text-slate-600" data-testid={`sample-brand-website-${brand.toLowerCase()}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Button type="submit" disabled={saving} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase h-11" data-testid="save-dealership-btn">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : editingDlr ? 'Update Dealership' : 'Add Dealership'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Template Editor Dialog ── */}
      <Dialog open={showTplEditor} onOpenChange={(open) => { setShowTplEditor(open); if (!open) { setShowTplPreview(false); setShowLogoLibrary(false); } }}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wider">
              {showTplPreview ? 'Preview Template' : 'Edit Template'}
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">Customize template content, logos, and email buttons.</DialogDescription>
          </DialogHeader>
          {!showTplPreview && (
            <div className="flex justify-end -mt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowTplPreview(true)} className="border-[#333] text-xs font-bold tracking-wider uppercase text-slate-400" data-testid="tpl-preview-btn">
                <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview
              </Button>
            </div>
          )}

          {showTplPreview ? (
            <div className="mt-2 space-y-4" data-testid="tpl-preview-panel">
              <div className="p-6 bg-white rounded-lg overflow-hidden">
                {tplForm.logo_url && <div className="text-center mb-4"><img src={tplForm.logo_url} alt="Logo" className="max-h-20 inline-block" /></div>}
                <div className="template-preview text-sm text-gray-800 leading-relaxed prose prose-sm max-w-none" style={{ color: '#1a1a1a' }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(tplForm.body.replace(/\{\{(\w+)\}\}/g, (_, k) => `<span style="color:#7c3aed;background:#f3f0ff;padding:2px 4px;border-radius:3px;font-size:11px">{{${k}}}</span>`)) }}
                  data-testid="tpl-preview-body" />
              </div>
              <div className="flex justify-end">
                <Button type="button" variant="outline" size="sm" onClick={() => setShowTplPreview(false)} className="border-[#333] text-xs font-bold tracking-wider uppercase text-slate-400">
                  <Edit2 className="w-3.5 h-3.5 mr-1.5" /> Back to Editor
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={saveTemplate} className="space-y-4 mt-4" data-testid="tpl-editor-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Template Name</Label>
                  <Input value={tplForm.name} onChange={e => setTplForm(p => ({ ...p, name: e.target.value }))} className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm" data-testid="tpl-name-input" required />
                </div>
                <div>
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Lead Type</Label>
                  <Input value={editingTpl?.lead_type ? (LEAD_TYPES.find(t => t.value === editingTpl.lead_type)?.label || editingTpl.lead_type) : ''} disabled className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm opacity-50" />
                </div>
              </div>

              {/* Logo */}
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase flex items-center gap-2"><Image className="w-3 h-3" /> Logo</Label>
                  <button type="button" onClick={() => { setShowLogoLibrary(!showLogoLibrary); if (!showLogoLibrary) fetchLogoLib(); }} className="text-[10px] font-bold tracking-wider text-slate-500 hover:text-red-400 uppercase transition-colors flex items-center gap-1" data-testid="tpl-logo-library-toggle">
                    <ImagePlus className="w-3 h-3" /> {showLogoLibrary ? 'Hide Library' : 'Logo Library'}
                  </button>
                </div>
                {tplForm.logo_url && !showLogoLibrary && (
                  <div className="mt-2 flex items-center gap-3 p-3 bg-[#111] border border-[#262626] rounded-lg">
                    <img src={tplForm.logo_url} alt="" className="h-12 object-contain rounded" />
                    <span className="text-xs text-slate-400 flex-1 truncate">{tplForm.logo_url.split('/').pop()}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setTplForm(p => ({ ...p, logo_url: '' }))} className="text-slate-500 hover:text-red-400 h-8 px-2"><X className="w-4 h-4" /></Button>
                  </div>
                )}
                {showLogoLibrary && (
                  <div className="mt-2 p-4 bg-[#111] border border-[#262626] rounded-lg" data-testid="tpl-logo-library">
                    <div className="grid grid-cols-5 gap-3">
                      {logoLibrary.map(logo => (
                        <div key={logo.logo_id} className={`relative group cursor-pointer rounded-lg border-2 p-2 flex items-center justify-center h-20 transition-all ${tplForm.logo_url === logo.url ? 'border-red-500 bg-red-500/10' : 'border-[#333] bg-[#0A0A0A] hover:border-[#555]'}`}
                          onClick={() => setTplForm(p => ({ ...p, logo_url: logo.url }))}>
                          <img src={logo.url} alt={logo.original_name} className="max-h-14 max-w-full object-contain" />
                          {tplForm.logo_url === logo.url && <div className="absolute top-1 right-1 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                        </div>
                      ))}
                      <label className={`cursor-pointer rounded-lg border-2 border-dashed border-[#333] hover:border-red-600/40 p-2 flex flex-col items-center justify-center h-20 transition-all ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                        {uploading ? <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin" /> : <><Upload className="w-5 h-5 text-slate-500 mb-1" /><span className="text-[9px] font-bold tracking-wider text-slate-500 uppercase">Upload</span></>}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => { handleLogoUpload(e.target.files?.[0]); e.target.value = ''; }} />
                      </label>
                    </div>
                  </div>
                )}
                {!tplForm.logo_url && !showLogoLibrary && (
                  <button type="button" onClick={() => { setShowLogoLibrary(true); fetchLogoLib(); }} className="mt-1.5 w-full p-3 border border-dashed border-[#333] rounded-lg text-slate-500 hover:text-red-400 hover:border-red-600/30 transition-all text-xs font-bold tracking-wider uppercase flex items-center justify-center gap-2" data-testid="tpl-add-logo-btn">
                    <ImagePlus className="w-4 h-4" /> Select or Upload Logo
                  </button>
                )}
              </div>

              {/* Subject */}
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Subject Line</Label>
                <Input value={tplForm.subject} onChange={e => setTplForm(p => ({ ...p, subject: e.target.value }))} placeholder="Thank you for your interest..." className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600" data-testid="tpl-subject-input" />
              </div>

              {/* Template Variables */}
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-2 block">Insert Variable</Label>
                <div className="flex flex-wrap gap-1">
                  {TEMPLATE_VARS.map(v => (
                    <button key={v.key} type="button" onClick={() => insertVariable(v.key)} className="px-2 py-1 text-[10px] font-mono bg-[#111] border border-[#333] rounded text-slate-400 hover:text-red-400 hover:border-red-600/30 transition-colors" data-testid={`tpl-var-${v.key.replace(/[{}]/g,'')}`}>
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Rich Text Editor */}
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-2 block">Body</Label>
                <div className="bg-[#111] border border-[#333] rounded-lg overflow-hidden [&_.ql-toolbar]:bg-[#111] [&_.ql-toolbar]:border-[#333] [&_.ql-container]:border-[#333] [&_.ql-editor]:text-white [&_.ql-editor]:min-h-[300px] [&_.ql-snow_.ql-stroke]:stroke-slate-400 [&_.ql-snow_.ql-fill]:fill-slate-400 [&_.ql-snow_.ql-picker]:text-slate-400 [&_.ql-snow_.ql-picker-options]:bg-[#171717] [&_.ql-snow_.ql-picker-options]:border-[#333]">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={tplForm.body}
                    onChange={v => setTplForm(p => ({ ...p, body: v }))}
                    modules={quillModules}
                    formats={quillFormats}
                  />
                </div>
              </div>

              {/* Email Button Toggles */}
              {editingTpl && editingTpl.lead_type !== 'general_inquiry' && (
                <div>
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-3 block">Email Buttons</Label>
                  <div className="grid grid-cols-2 gap-3 p-3 bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg">
                    {editingTpl.lead_type !== 'used_vehicle_inquiry' && editingTpl.lead_type !== 'used_vehicle_inquiry_cpo' && (<>
                      {editingTpl.lead_type === 'new_vehicle_inquiry' ? (
                      <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                        <span className="text-xs text-slate-300">New Vehicle Inventory</span>
                        <Switch checked={tplForm.show_new_inventory_btn || false} onCheckedChange={v => setTplForm(p => ({ ...p, show_new_inventory_btn: v }))} />
                      </div>
                      ) : (
                      <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                        <span className="text-xs text-slate-300">Window Sticker</span>
                        <Switch checked={tplForm.show_window_sticker} onCheckedChange={v => setTplForm(p => ({ ...p, show_window_sticker: v }))} />
                      </div>
                      )}
                      <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                        <span className="text-xs text-slate-300">Warranty</span>
                        <Switch checked={tplForm.show_warranty_btn} onCheckedChange={v => setTplForm(p => ({ ...p, show_warranty_btn: v }))} />
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                        <span className="text-xs text-slate-300">Similar Vehicles</span>
                        <Switch checked={tplForm.show_similar_vehicles_btn} onCheckedChange={v => setTplForm(p => ({ ...p, show_similar_vehicles_btn: v }))} />
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                        <span className="text-xs text-slate-300">Test Drive</span>
                        <Switch checked={tplForm.show_test_drive_btn} onCheckedChange={v => setTplForm(p => ({ ...p, show_test_drive_btn: v }))} data-testid="tpl-toggle-test-drive" />
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                        <span className="text-xs text-slate-300">Pre-Owned</span>
                        <Switch checked={tplForm.show_preowned_btn} onCheckedChange={v => setTplForm(p => ({ ...p, show_preowned_btn: v }))} />
                      </div>
                    </>)}
                    {(editingTpl.lead_type === 'used_vehicle_inquiry' || editingTpl.lead_type === 'used_vehicle_inquiry_cpo') && (<>
                      <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                        <span className="text-xs text-slate-300">Pre-Owned</span>
                        <Switch checked={tplForm.show_preowned_btn} onCheckedChange={v => setTplForm(p => ({ ...p, show_preowned_btn: v }))} />
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                        <span className="text-xs text-slate-300">Test Drive</span>
                        <Switch checked={tplForm.show_test_drive_btn} onCheckedChange={v => setTplForm(p => ({ ...p, show_test_drive_btn: v }))} data-testid="tpl-toggle-test-drive-used" />
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                        <span className="text-xs text-slate-300">CPO Benefits</span>
                        <Switch checked={tplForm.show_cpo_benefits} onCheckedChange={v => setTplForm(p => ({ ...p, show_cpo_benefits: v }))} />
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                        <span className="text-xs text-slate-300">CPO vs. Used</span>
                        <Switch checked={tplForm.show_cpo_vs_used} onCheckedChange={v => setTplForm(p => ({ ...p, show_cpo_vs_used: v }))} />
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                        <span className="text-xs text-slate-300">CPO Inspection</span>
                        <Switch checked={tplForm.show_cpo_inspection} onCheckedChange={v => setTplForm(p => ({ ...p, show_cpo_inspection: v }))} />
                      </div>
                    </>)}
                  </div>
                </div>
              )}

              <Button type="submit" disabled={saving} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase h-11" data-testid="save-template-btn">
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Save Template'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ── History Detail Dialog ── */}
      <Dialog open={!!showHistoryDetail} onOpenChange={() => setShowHistoryDetail(null)}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wider">Simulation Detail</DialogTitle>
          </DialogHeader>
          {showHistoryDetail && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-[#111] border border-[#262626] rounded-lg">
                  <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-1">Customer</p>
                  <p className="text-sm text-white">{showHistoryDetail.customer_name || 'N/A'}</p>
                  <p className="text-xs text-slate-400">{showHistoryDetail.customer_email}</p>
                </div>
                {(showHistoryDetail.vehicle_year || showHistoryDetail.vehicle_make || showHistoryDetail.vehicle_model || showHistoryDetail.vin) && (
                <div className="p-3 bg-[#111] border border-[#262626] rounded-lg">
                  <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-1">Vehicle</p>
                  <p className="text-sm text-white">{showHistoryDetail.vehicle_year} {showHistoryDetail.vehicle_make} {showHistoryDetail.vehicle_model}</p>
                  {showHistoryDetail.vin && <p className="text-xs text-slate-400 font-mono">{showHistoryDetail.vin}</p>}
                </div>
                )}
              </div>
              <div className="p-3 bg-[#111] border border-[#262626] rounded-lg">
                <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-1">Subject</p>
                <p className="text-sm text-white">{showHistoryDetail.response_subject}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-1">Email Preview</p>
                <div className="bg-white rounded-lg p-4 max-h-96 overflow-y-auto" style={{ color: '#1a1a1a' }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(showHistoryDetail.response_body) }} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Response Preview Popout Dialog ── */}
      <Dialog open={showResponsePreview && !!simResult} onOpenChange={setShowResponsePreview}>
        <DialogContent className="bg-white border-[#ddd] text-[#1a1a1a] max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#1a1a1a]">{simResult?.response_subject || 'Email Preview'}</DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">Full email preview as the recipient would see it.</DialogDescription>
          </DialogHeader>
          {simResult && (
            <div className="mt-2">
              <div
                className="prose prose-sm max-w-none"
                style={{ color: '#1a1a1a' }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(simResult.response_body) }}
                data-testid="popout-email-preview"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
